import { Editor } from "slate";
import { deserialize } from "./deserialize";
import { CustomEditor, ImgElement } from "../slate-types";

const VOID_ELEMENTS = ["img", "mention", "lineBreak"];
const INLINE_ELEMENTS = ["link", "mention", "lineBreak"];

const withDragAndDrop = (editor: CustomEditor) => {
  const { insertData } = editor;

  editor.insertData = (data) => {
    // Check if this is our custom image drag operation
    try {
      if (data.types.includes("application/slate-path-fragment")) {
        const text = data.getData("application/slate-path-fragment");
        const { type } = JSON.parse(text);

        if (type === "image-drag") {
          // This is handled by custom drop handler in RichEditor
          return;
        }
      }
    } catch (error) {
      console.error("Error in insertData:", error);
    }

    // Otherwise use the default handler
    insertData(data);
  };

  return editor;
};

export const withPlugins = (editor: CustomEditor): CustomEditor => {
  const { insertData, isInline, isVoid, markableVoid } = editor;
  editor = withDragAndDrop(editor);
  editor.isInline = (element) => {
    return INLINE_ELEMENTS.includes(element.type) ? true : isInline(element);
  };

  editor.isVoid = (element) => {
    return VOID_ELEMENTS.includes(element.type) ? true : isVoid(element);
  };

  editor.markableVoid = (element) => {
    return element.type === "mention" || markableVoid(element);
  };

  editor.insertData = (data) => {
    const html = data.getData("text/html");
    if (html) {
      const parsed = new DOMParser().parseFromString(html, "text/html");
      // default behavior for pasting Slate nodes
      const firstDiv = parsed.body.querySelector("div");
      if (firstDiv?.dataset?.slateNode) {
        insertData(data);
        return;
      }
      // check if is pasted from Outlook - trim excess line breaks
      let trimLineBreaks = false;
      if (parsed.children?.[0].getAttribute("xmlns:o") === "urn:schemas-microsoft-com:office:office") {
        trimLineBreaks = true;
      }
      const fragment = deserialize(parsed.body, trimLineBreaks);
      Editor.insertFragment(editor, fragment);
      return;
    }

    if (data.files?.length > 0) {
      for (const file of Array.from(data.files)) {
        const reader = new FileReader();
        const [mime] = file.type.split("/");

        if (mime === "image") {
          reader.addEventListener("load", () => {
            const src = reader.result as string;

            const image: ImgElement = { type: "img", src, alt: file.name, children: [{ text: "" }] };
            Editor.insertNode(editor, image);
          });

          reader.readAsDataURL(file);
        }
      }
    }
    insertData(data);
  };

  return editor;
};
